import { Delete, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { medicalsApi } from "../../../../../../../api/medicalsApi";
import { QueryKeys } from "../../../../../../../constants/queryKeys";
import { useExecutor } from "../../../../../../../hooks/useExecutor";
import useNotifications from "../../../../../../../hooks/useNotifications";
import { useUnsavedWhileEditing } from "../../../../../../../hooks/useUnsavedWhileEditing";
import { CopilotManager } from "../../../../../../../managers/CopilotManager";
import {
  TCopilot,
  TCopilotNoteAllergy,
} from "../../../../../../../types/copilot";
import {
  useOrganization,
  useOrganizationReference,
} from "../../../../../../facility/hooks/useOrganization";
import { useCopilotClinicalContext } from "../../useCopilotClinicalContext";
import CopilotSectionAcceptButton from "./CopilotSectionAcceptButton";
import CopilotSectionEditedBadge from "./CopilotSectionEditedBadge";
import { getAllergiesPopulateNotification } from "../utils/processCopilotAllergies";
import {
  isSectionHumanEdited,
  withSectionHumanEditedExtension,
} from "../utils/copilotExtensions";

interface EditableAllergiesProps {
  allergies?: TCopilotNoteAllergy[] | null;
  readOnly?: boolean;
}

function formatAllergyReaction(reaction?: string | string[] | null): string {
  if (!reaction) return "";
  return Array.isArray(reaction) ? reaction.join(", ") : reaction;
}

function hasAllergyData(allergies?: TCopilotNoteAllergy[] | null): boolean {
  return (
    Array.isArray(allergies) &&
    allergies.some((a) => a?.substance || formatAllergyReaction(a?.reaction))
  );
}

const EditableAllergies = ({
  allergies,
  readOnly = false,
}: EditableAllergiesProps) => {
  const [isEditing, setIsEditing] = useState(false);
  useUnsavedWhileEditing("copilot-note-allergies", isEditing);
  const [editedAllergies, setEditedAllergies] = useState<TCopilotNoteAllergy[]>(
    allergies || [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const organizationReference = useOrganizationReference();
  const { countryCode } = useOrganization();
  const queryClient = useQueryClient();
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { executeCallback } = useExecutor();
  const { setNotification } = useNotifications();

  useEffect(() => {
    if (!isEditing) {
      setEditedAllergies(allergies || []);
    }
  }, [allergies, isEditing]);

  const handleEditClick = () => {
    if (!copilotData) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!copilotData?.id) return;

    try {
      setIsSaving(true);

      const updatedNote: TCopilot = withSectionHumanEditedExtension(
        {
          ...copilotData,
          note: {
            ...copilotData.note,
            history: {
              ...copilotData.note?.history,
              allergies: editedAllergies,
            },
          },
        },
        "allergies",
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setEditedAllergies(response.data?.note?.history?.allergies || []);
      setNotification({
        message: "Allergies updated successfully",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Failed to update allergies",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const allergiesHumanEdited = isSectionHumanEdited(copilotData, "allergies");

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Allergies</Typography>
          <CopilotSectionEditedBadge section="allergies" />
          {!isEditing && !readOnly && (
            <IconButton size="small" onClick={handleEditClick}>
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {!isEditing &&
          !readOnly &&
          hasLinkedEncounter &&
          hasAllergyData(allergies) && (
            <CopilotSectionAcceptButton
              sections={["allergies"]}
              populationStatusField="allergies"
              onAccept={async () => {
                if (
                  !encounterReference?.identifier ||
                  !organizationReference?.identifier
                ) {
                  setNotification({
                    message:
                      "Link this note to a medical encounter to apply content to the chart.",
                    variant: "error",
                  });
                  throw new Error("Encounter not linked");
                }

                const result = await CopilotManager.populateAllergies(
                  encounterReference.identifier,
                  organizationReference.identifier,
                  countryCode,
                  { overwriteReactions: allergiesHumanEdited },
                );
                await queryClient.invalidateQueries({
                  queryKey: [QueryKeys.ALLERGIES],
                });
                if (
                  patientReference?.identifier &&
                  encounterReference?.identifier
                ) {
                  await executeCallback(
                    "copilot_refresh_allergies",
                    null,
                    patientReference.identifier,
                    encounterReference.identifier,
                  );
                }
                const notification = getAllergiesPopulateNotification(result);
                setNotification(notification);
                if (notification.variant !== "success") {
                  throw new Error(notification.message);
                }
              }}
            />
          )}
      </Box>

      {isEditing ? (
        <Box sx={{ mt: 1 }}>
          {editedAllergies.map((allergy, index) => (
            <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Allergen"
                value={allergy?.substance || ""}
                onChange={(e) => {
                  const next = [...editedAllergies];
                  next[index] = { ...next[index], substance: e.target.value };
                  setEditedAllergies(next);
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Reaction"
                value={formatAllergyReaction(allergy?.reaction)}
                onChange={(e) => {
                  const next = [...editedAllergies];
                  next[index] = { ...next[index], reaction: e.target.value };
                  setEditedAllergies(next);
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setEditedAllergies(
                    editedAllergies.filter((_, i) => i !== index),
                  );
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            onClick={() => {
              setEditedAllergies([
                ...editedAllergies,
                { substance: "", reaction: "" },
              ]);
            }}
          >
            Add Item
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setEditedAllergies(allergies || []);
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              loading={isSaving}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 1 }}>
          {allergies && allergies.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
              {allergies.map((allergy, index) => (
                <li key={index}>
                  <Typography variant="body2">
                    {allergy?.substance} -{" "}
                    {formatAllergyReaction(allergy?.reaction)}
                  </Typography>
                </li>
              ))}
            </ul>
          ) : (
            <Typography variant="body2" color="GrayText">
              No allergies recorded
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EditableAllergies;
